"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SegmentClient = void 0;
/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable no-console */
var events_1 = require("events");
var consola_1 = __importStar(require("consola"));
/**
 * This is the wrapper around the Segment client that allows us to queue events until the library
 * has successfully loaded. If events are tracked before the client has loaded, they will be queued
 * up and triggered once it's ready.
 */
var SegmentClient = /** @class */ (function () {
    function SegmentClient() {
        this.logger = consola_1.default.create({
            defaults: {
                tag: 'Analytics',
            },
            level: consola_1.LogLevel.Info,
        });
        this.emitter = new events_1.EventEmitter();
        this.defaultTrackProperties = {};
    }
    /**
     * Set the analytics client. This should only be called once.
     * @param analytics
     */
    SegmentClient.prototype.initialize = function (analytics) {
        if (this.client)
            return;
        this.client = analytics;
        this.emitter.emit('initialize');
    };
    /**
     * Set the client options
     */
    SegmentClient.prototype.setOptions = function (options) {
        this.logger.level = options.debug ? consola_1.LogLevel.Info : consola_1.LogLevel.Error;
        this.defaultTrackProperties = options.defaultTrackProperties || {};
    };
    /**
     * Track a page view
     * @param name The name of the page
     * @param properties Any properties describing this page
     * @param options Segment options
     * @param callback
     * @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#page
     */
    SegmentClient.prototype.page = function (event) {
        var _this = this;
        if (event === void 0) { event = {}; }
        return new Promise(function (resolve, reject) {
            var name = event.name, category = event.category, properties = event.properties;
            if (_this.client) {
                _this.logger.debug('Page track start', event);
                try {
                    _this.client.page(category, name, properties, function () {
                        _this.logger.info('Page track successful', event);
                        resolve();
                    });
                }
                catch (error) {
                    _this.logger.error('Page track failed');
                    reject(error);
                }
            }
            else {
                _this.emitter.once('initialize', function () {
                    _this.page(event);
                });
                resolve();
            }
        });
    };
    /**
     * Identify the current user. Whenever this is called, all future track events will
     * be associated with this user.
     * @param userId The uuid of the user
     * @param traits Any extra traits about this person to update in Segment
     * @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#identify
     */
    SegmentClient.prototype.identify = function (event) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var userId = event.userId, traits = event.traits;
            if (_this.client) {
                _this.logger.debug('Identify start', event);
                try {
                    _this.client.identify(userId, traits, function () {
                        _this.logger.info('Identify succesful', event);
                        resolve();
                    });
                }
                catch (error) {
                    _this.logger.error('Identify failed');
                    reject(error);
                }
            }
            else {
                _this.emitter.once('initialize', function () {
                    _this.identify(event);
                });
                resolve();
            }
        });
    };
    /**
     * The alias method combines two previously unassociated user identities. Aliasing is generally handled automatically
     * when you identify a user. However, some tools require an explicit alias call.
     *
     * This is an advanced method, but it is required to manage user identities successfully in some of our destinations.
     * Most notably, alias is necessary for properly implementing KISSmetrics and Mixpanel.
     *
     * @param userId The uuid of the user
     * @param traits Any extra traits about this person to update in Segment
     * @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#alias
     */
    SegmentClient.prototype.alias = function (event) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var userId = event.userId, previousId = event.previousId;
            if (_this.client) {
                _this.logger.debug('Alias', event);
                try {
                    _this.client.alias(userId, previousId, function () {
                        _this.logger.info('Alias succesful', event);
                        resolve();
                    });
                }
                catch (error) {
                    _this.logger.error('Alias failed');
                    reject(error);
                }
            }
            else {
                _this.emitter.once('initialize', function () {
                    _this.alias(event);
                });
            }
        });
    };
    /**
     * Track an event representing an action by the user on the page.
     * @param event The name of the event to send to Segment. Make sure you follow the style guide for event names
     * @param properties Properties associated with the event
     * @param options
     * @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#track
     */
    SegmentClient.prototype.track = function (track) {
        var _this = this;
        var defaultTrackProperties = this.defaultTrackProperties;
        return new Promise(function (resolve, reject) {
            var event = track.event, properties = track.properties;
            if (_this.client) {
                _this.logger.debug('Track started', track);
                try {
                    _this.client.track(event, __assign(__assign({}, defaultTrackProperties), properties), function () {
                        _this.logger.info('Track successful', track);
                        resolve();
                    });
                }
                catch (e) {
                    _this.logger.error('Track failed');
                    reject(e);
                }
            }
            else {
                _this.emitter.once('initialize', function () {
                    _this.track(track);
                });
                resolve();
            }
        });
    };
    /**
     * This will associate the current user with this group
     * @param name The name of the group to associate the current user with
     * @param traits Traits about this group
     * @param options
     * @param callback
     * @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#group
     */
    SegmentClient.prototype.group = function (event) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var groupId = event.groupId, traits = event.traits;
            if (_this.client) {
                _this.logger.debug('Group started', event);
                try {
                    _this.client.group(groupId, traits, function () {
                        _this.logger.info('Group succesful', event);
                        resolve();
                    });
                }
                catch (error) {
                    _this.logger.error('Group failed');
                    reject(error);
                }
            }
            else {
                _this.emitter.once('initialize', function () {
                    _this.group(event);
                });
            }
        });
    };
    /**
     * The ready method allows you to pass in a callback that is called once all enabled destinations load, and once
     * analytics.js finishes initializing.
     * @param callback
     * @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#ready
     */
    SegmentClient.prototype.ready = function (callback) {
        var _this = this;
        return new Promise(function (resolve) {
            if (_this.client) {
                _this.client.ready(function () {
                    if (callback && _this.client)
                        callback(_this.client);
                    resolve();
                });
            }
            else {
                _this.emitter.once('initialize', function () {
                    _this.ready(callback);
                });
            }
        });
    };
    /**
     * The global analytics object emits events whenever you call alias, group, identify, track or page.
     * @param method
     * @param callback
     */
    SegmentClient.prototype.on = function (method, callback) {
        var _this = this;
        if (this.client) {
            this.client.on(method, callback);
        }
        else {
            this.emitter.once('initialize', function () {
                _this.on(method, callback);
            });
        }
    };
    /**
     * Set the ID for the anonymous
     * @param id
     */
    SegmentClient.prototype.setAnonymousId = function (id) {
        var _this = this;
        return new Promise(function (resolve) {
            if (_this.client) {
                _this.client.setAnonymousId(id);
            }
            else {
                _this.emitter.once('initialize', function () {
                    _this.setAnonymousId(id);
                    resolve();
                });
            }
        });
    };
    return SegmentClient;
}());
exports.SegmentClient = SegmentClient;
